import { useEffect, useRef, useState } from 'react';
import PageHeader from './sections/PageHeader';
import Banner from './sections/PageBanner';
import Footer from './sections/PageFooter';
import ReactGA from 'react-ga4';

ReactGA.initialize('G-FMCF2WQ3F6');

/* ── Ways to get involved ───────────────────────────────────────────── */
const ways = [
  {
    image:    '/images/marine.jpg',
    eyebrow:  'Field & Community',
    title:    'Become a Volunteer',
    body:     'Join rangers, community scouts, and citizen scientists on the ground. Help document incidents, run outreach programmes, and feed real data into the WITS intelligence network.',
    cta:      'Volunteer with Us',
    href:     'mailto:info@lapa.africa?subject=Becoming a WITS Volunteer',
    external: true,
  },
  {
    image:    '/images/penguin.jpg',
    eyebrow:  'Financial Support',
    title:    'Fund the Mission',
    body:     'Every contribution directly funds field operations, technology development, and community training. Your donation makes impunity harder and accountability inevitable.',
    cta:      'Make a Donation',
    href:     '#donate',
    external: false,
  },
  {
    image:    '/images/leopard.jpg',
    eyebrow:  'Institutional',
    title:    'Partner with Us',
    body:     "NGOs, government agencies, research institutions, and law firms — WITS is built for collaboration. Let's pool data, share expertise, and drive policy change together.",
    cta:      'Explore Partnership',
    href:     'mailto:info@lapa.africa?subject=WITS Partnership Enquiry',
    external: true,
  },
  {
    image:    '/images/elephant.jpg',
    eyebrow:  'Legacy Giving',
    title:    'Leave a Gift in Your Will',
    body:     'Join thousands worldwide who have chosen to leave a legacy gift to conservation. A planned gift to WITS ensures that protection of wildlife continues for generations.',
    cta:      'Learn About Legacy Giving',
    href:     'mailto:info@lapa.africa?subject=Legacy Gift Enquiry',
    external: true,
  },
];

/* ── Scroll-reveal hook ─────────────────────────────────────────────── */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return { ref, visible };
}

/* ── Page hero ──────────────────────────────────────────────────────── */
function PageHero() {
  return (
    <div
      className="relative w-full overflow-hidden flex flex-col justify-end"
      style={{ height: '58vh', minHeight: 400, background: '#052e16' }}
    >
      <img
        src="/images/support.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover select-none"
        style={{ opacity: 0.38 }}
      />
      <div className="absolute inset-0 bg-black/40 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#052e16]/20 via-transparent to-[#052e16]/92 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-14">
        <span className="flex items-center gap-3 mb-4">
          <span className="h-px w-8 bg-green-400" />
          <span className="text-green-400 text-xs tracking-[0.22em] uppercase font-medium" style={{ fontFamily: 'var(--font-body)' }}>
            Take Action
          </span>
        </span>
        <h1
          className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-4"
          style={{ fontFamily: 'var(--font-display)', textShadow: '0 2px 24px rgba(0,0,0,0.55)' }}
        >
          Get <span className="text-green-400">Involved</span>
        </h1>
        <p
          className="text-white/65 text-base max-w-xl leading-relaxed"
          style={{ fontFamily: 'var(--font-body)', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
        >
          Wildlife protection is a collective effort. Whether you volunteer, donate, partner, or advocate
          — every action strengthens the network that keeps Africa&apos;s wildlife alive.
        </p>
      </div>
    </div>
  );
}

/* ── Involvement cards ──────────────────────────────────────────────── */
function WaysSection() {
  const { ref, visible } = useReveal();

  return (
    <section ref={ref} className="w-full bg-[#fafaf7] py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className={`max-w-2xl mb-16 reveal ${visible ? 'visible' : ''}`}>
          <span className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-green-600" />
            <span className="text-green-700 text-xs tracking-[0.2em] uppercase font-medium" style={{ fontFamily: 'var(--font-body)' }}>
              Four Ways to Help
            </span>
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-[1.1]" style={{ fontFamily: 'var(--font-display)' }}>
            Your role in protecting<br />
            <span className="text-green-700">Africa&apos;s natural heritage</span>
          </h2>
        </div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {ways.map((w, i) => (
            <div
              key={w.title}
              className={`group flex flex-col rounded-2xl overflow-hidden border border-gray-200 bg-white hover:border-green-300 hover:shadow-xl hover:shadow-green-100/50 transition-all duration-500 hover:-translate-y-1 reveal reveal-delay-${i + 1} ${visible ? 'visible' : ''}`}
            >
              {/* Image */}
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={w.image}
                  alt={w.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#052e16]/60 to-transparent" />
                <span
                  className="absolute top-4 left-4 px-3 py-1.5 rounded-lg text-green-400 text-[10px] tracking-[0.15em] uppercase font-semibold"
                  style={{ fontFamily: 'var(--font-body)', background: 'rgba(5,46,22,0.85)', border: '1px solid rgba(74,222,128,0.25)', backdropFilter: 'blur(6px)' }}
                >
                  {w.eyebrow}
                </span>
              </div>

              {/* Body */}
              <div className="flex flex-col flex-1 p-7">
                <h3
                  className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-800 transition-colors"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {w.title}
                </h3>
                <p
                  className="text-gray-500 text-sm leading-relaxed flex-1 mb-6"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  {w.body}
                </p>
                <a
                  href={w.href}
                  {...(w.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="self-start inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-700 text-white text-xs font-semibold hover:bg-green-600 transition-all duration-300 hover:shadow-md hover:shadow-green-200 hover:-translate-y-0.5"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  {w.cta} &rarr;
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Dark CTA band ──────────────────────────────────────────────────── */
function CTABand() {
  const { ref, visible } = useReveal();
  return (
    <section
      ref={ref}
      className="w-full py-20"
      style={{ background: '#052e16' }}
    >
      <div className={`max-w-4xl mx-auto px-6 lg:px-10 text-center reveal ${visible ? 'visible' : ''}`}>
        <p className="text-green-400 text-xs tracking-[0.2em] uppercase font-medium mb-4" style={{ fontFamily: 'var(--font-body)' }}>
          Ready to act?
        </p>
        <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-5" style={{ fontFamily: 'var(--font-display)' }}>
          Every data point matters.<br />Every voice counts.
        </h2>
        <p className="text-white/55 text-base leading-relaxed mb-8 max-w-lg mx-auto" style={{ fontFamily: 'var(--font-body)' }}>
          You don&apos;t need a uniform or a lab coat to protect wildlife. You need a phone, a connection,
          and the will to report what you see.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href="/report-incident"
            className="px-7 py-3.5 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-green-900/40"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Report an Incident &rarr;
          </a>
          <a
            href="/contact-us"
            className="px-7 py-3.5 rounded-full border border-white/30 text-white text-sm font-medium hover:border-white/60 hover:bg-white/8 transition-all duration-300"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Contact the Team
          </a>
        </div>
      </div>
    </section>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */
export default function GetInvolved() {
  ReactGA.send({ hitType: 'pageview', page: '/get-involved', title: 'Get Involved' });
  return (
    <>
      <Banner />
      <PageHeader />
      <PageHero />
      <WaysSection />
      <CTABand />
      <Footer />
    </>
  );
}
